<template>
  <!-- Forms Container -->
  <div class="forms-container">
    <div class="form-wrapper">
      <div class="form-card mt-0 p-3">
        <!-- Single container for all attributes -->
        <div class="section-container border-primary shadow-lg">

          <div v-if="is_loading" class="text-center py-5">
            <div class="spinner-border text-secondary" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <p class="mt-2 mb-0 text-muted text-xxs">Loading attributes…</p>
          </div>

          <!-- Empty state -->
          <div v-else-if="attributes.length === 0" class="alert alert-info rounded-3 text-xxs mb-0">
            {{ empty_message || 'No attributes are assigned to this profile yet.' }}
          </div>
          <!-- Dynamic sections for each attribute -->
          <div v-else>
            <div v-for="attribute in attributes" :key="attribute.id">
              <div class="d-flex align-items-start gap-2">
                <div class="flex-grow-1">
                  <div class="section-header mt-1 text-xs d-flex justify-content-between align-items-center">
                      <div class="d-flex align-items-center">
                          {{ attribute.question_text }}
                      </div>

                    <!-- Right-aligned checkboxes (badge removed) -->
                    <div class="d-flex flex-column align-items-end gap-2">
                      <!-- Checkboxes -->
                      <div v-if="user_role === 'System Admin'" class="section-checkboxes d-flex gap-2">
                        <div class="form-check">
                          <input class="form-check-input shadow-sm" type="checkbox" :id="`attribute-${attribute.id}-checkbox1`">
                          <label class="form-check-label text-xxs" :for="`attribute-${attribute.id}-checkbox1`">Auto Verify</label>
                        </div>
                        <div v-if="!attribute.is_verified" class="form-check">
                          <input class="form-check-input shadow-sm" type="checkbox" :id="`attribute-${attribute.id}-checkbox2`">
                          <label class="form-check-label text-xxs" :for="`attribute-${attribute.id}-checkbox2`">Manual Verify</label>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="form-fields-container mt-3">
                    <div class="form-group">
                      <!-- Display answer based on question type -->

                      <!-- Short Text Input -->
                      <div v-if="attribute.question_type === 'short_text'">
                        <input
                          v-model="attribute.answer_value"
                          type="text"
                          class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                          :placeholder="'Enter ' + attribute.question_text.toLowerCase()"
                          @input="update_answer(attribute.id, $event.target.value)"
                        />
                      </div>

                      <!-- Long Text Input -->
                      <div v-else-if="attribute.question_type === 'long_text'">
                        <textarea
                          v-model="attribute.answer_value"
                          class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                          :placeholder="'Enter ' + attribute.question_text.toLowerCase()"
                          :rows="3"
                          @input="update_answer(attribute.id, $event.target.value)"
                        ></textarea>
                      </div>

                      <!-- Number Input -->
                      <div v-else-if="String(attribute.question_type).toLowerCase() === 'number'">
                        <input
                          type="number"
                          class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                          v-model.number="attribute.answer_value"
                          :placeholder="'Enter ' + attribute.question_text.toLowerCase()"
                          :step="attribute.step || 'any'"
                          :min="attribute.min ?? null"
                          :max="attribute.max ?? null"
                          @input="update_answer(attribute.id, attribute.answer_value)"
                        />
                      </div>

                      <!-- Date Input -->
                      <div v-else-if="String(attribute.question_type).toLowerCase() === 'date'">
                        <input
                          type="date"
                          class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                          v-model="attribute.answer_value"
                          :placeholder="'Select ' + attribute.question_text.toLowerCase()"
                          @input="update_answer(attribute.id, attribute.answer_value)"
                        />
                      </div>


                      <!-- multi_select direct from API shape -->
                      <div v-else-if="attribute.question_type === 'multi_select'">
                        <div v-for="(option, opt_index) in attribute.question?.choices || []" :key="'checkbox-' + opt_index" class="form-check">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :id="`attribute-${attribute.id}-${opt_index}`"
                            :value="option.choice"
                            v-model="attribute.answer_value"
                            @change="update_answer(attribute.id, attribute.answer_value)"
                          />
                          <label class="form-check-label text-xxs" :for="`attribute-${attribute.id}-${opt_index}`">
                            {{ option.choice }}
                          </label>
                        </div>
                      </div>

                      <!-- single_select direct from API shape -->
                      <div v-else-if="attribute.question_type === 'single_select'">
                        <div v-for="(option, opt_index) in attribute.question?.choices || []" :key="'radio-' + opt_index" class="form-check">
                          <input
                            class="form-check-input"
                            type="radio"
                            :name="`attribute-${attribute.id}`"
                            :id="`attribute-${attribute.id}-${opt_index}`"
                            v-model="attribute.answer_value"
                            :value="option.choice"
                            :required="attribute.required"
                            @change="update_answer(attribute.id, attribute.answer_value)"
                          />
                          <label class="form-check-label text-xxs" :for="`attribute-${attribute.id}-${opt_index}`">
                            {{ option.choice }}
                          </label>
                        </div>
                      </div>


                      <!-- File Upload -->
                      <div v-else-if="attribute.question_type === 'file'">
                        <div v-if="attribute.files && attribute.files.length > 0">
                          <div class="d-flex align-items-center gap-2">
                            <span class="text-xs">{{ get_file_name(attribute.files[0]) }}</span>
                            <a
                              class="btn btn-primary-dark btn-sm rounded-small text-xxs text-dark"
                              :href="get_file_url(attribute.files[0])"
                              target="_blank"
                              rel="noopener"
                            >
                              Preview
                            </a>
                            <button
                              class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow ms-2"
                              type="button"
                              @click="remove_file(attribute.id)"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                        <div v-else>
                          <input
                            type="file"
                            class="form-control form-control-lg text-xxs bg-light border-1 py-1 rounded-lg"
                            @change="handle_file_upload($event, attribute.id)"
                          />
                        </div>
                      </div>

                      <!-- Display existing answer -->
                      <div v-else-if="attribute.answer_value">
                        <div class="answer-display bg-light p-2 rounded">
                          <p class="text-xs mb-0">{{ attribute.answer_value }}</p>
                        </div>
                      </div>

                      <!-- No answer provided -->
                      <div v-else>
                        <div class="no-answer text-muted">
                          <p class="text-xs mb-0">No Answer Provided</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- Admin buttons -->
                  <div class="d-flex justify-content-between align-items-end mt-3">
                    <div v-if="user_role === 'System Admin'" class="d-flex gap-2">
                      <button
                        v-if="!attribute.is_verified"
                        class="btn btn-primary-dark btn-sm text-xxs shadow"
                        @click="verify_attribute(attribute.id)"
                      >
                        Verify
                      </button>
                      <button
                        class="btn btn-primary-dark btn-sm text-xxs shadow"
                        data-bs-toggle="modal"
                        data-bs-target="#request_information"
                        @click="set_current_attribute(attribute)"
                      >
                        Request Information
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Removed divider -->
            </div>
          </div>


          <!-- Global action buttons inside the container -->
          <div class="d-flex justify-content-end align-items-center mt-4 pt-3 border-top">
            <div v-if="user_role === 'System Admin'" class="d-flex gap-2">
              <button class="btn btn-primary-dark btn-sm text-xxs shadow" @click="admin_action('approve')">
                Approve
              </button>
              <button class="btn btn-primary-dark btn-sm text-xxs shadow" @click="admin_action('reject')">
                Reject
              </button>
            </div>
          </div>
        </div>

        <!-- Submit Button -->
        <div class="d-flex justify-content-center mt-4" v-if="attributes.length && user_role !== 'System Admin'">
          <button class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark px-4" @click.prevent="submit_values()">
            Submit
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Request information modal -->
<teleport to="body">
  <div class="modal fade" id="request_information" tabindex="-1" aria-labelledby="request_information_label" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content bg-white rounded-3 p-0 shadow-lg">
        <div class="modal-header border-bottom-0 cute-header shadow-lg">
          <p class="modal-title text-white text-xxs" id="request_information_label">Request Information</p>
          <button type="button" class="modal-btn-close btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3" v-if="current_attribute">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <p class="text-xxs fw-bold mb-0">Question:</p>
            </div>
            <div class="mt-3">
              <div class="file-list-item">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="d-flex align-items-center">
                    <i class="bi bi-question-circle me-2 text-primary"></i>
                    <div>
                      <div class="text-xxs">{{ current_attribute.question_text }}</div>
                    </div>
                  </div>
                  <input type="checkbox" class="form-check-input ms-3" checked disabled>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-form-group form-group mb-2 d-flex flex-column">
            <label for="request_info_note" class="modal-form-label form-label text-start text-xxs">Notes</label>
            <textarea
              v-model="request_info_note"
              id="request_info_note"
              class="modal-form-control form-control text-xxs rounded-small p-2 shadow-sm"
              rows="4"
              placeholder="Add notes for the information request..."
            ></textarea>
          </div>
        </div>
        <div class="modal-footer border-0 p-2 d-flex justify-content-end">
          <button type="button" class="btn btn-primary-dark btn-sm text-xxs shadow" @click="request_information">
            Request
          </button>
        </div>
      </div>
    </div>
  </div>
</teleport>
</template>